import type { ReactNode } from 'react';

// Treść polityki prywatności (PL). Szablon RODO-zgodny z placeholderami
// w nawiasach kwadratowych — salon uzupełnia wartości przed wdrożeniem.
// Aby ułatwić podmianę, wszystkie placeholdery są spójne i wielkie literami.
//
// Placeholders do podmiany:
//   [NAZWA_SALONU]            np. "Studio Piękności Tatanga"
//   [ADRES_SALONU]            np. "ul. Kwiatowa 1, 00-000 Warszawa"
//   [NIP]                     np. "1234567890"
//   [EMAIL_KONTAKTOWY]        np. "kontakt@tatanga.eu"
//   [TELEFON]                 np. "+48 500 600 700"
//   [DATA_AKTUALIZACJI]       np. "9 maja 2026"

export function PrivacyContentPl(): ReactNode {
  return (
    <article className="space-y-6 text-sm leading-relaxed text-zinc-800 dark:text-zinc-200">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold text-zinc-900 dark:text-zinc-100">Polityka prywatności</h1>
        <p className="text-zinc-500 dark:text-zinc-400">
          Ostatnia aktualizacja: <strong>[DATA_AKTUALIZACJI]</strong>
        </p>
      </header>

      <section>
        <p>
          <strong>[NAZWA_SALONU]</strong> (dalej: „Salon") szanuje prywatność klientów i
          przetwarza dane osobowe zgodnie z Rozporządzeniem Parlamentu Europejskiego i Rady (UE)
          2016/679 z dnia 27 kwietnia 2016 r. (RODO) oraz polską ustawą o ochronie danych
          osobowych.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">1. Administrator danych</h2>
        <p>
          Administratorem Państwa danych osobowych jest <strong>[NAZWA_SALONU]</strong> z
          siedzibą pod adresem <strong>[ADRES_SALONU]</strong>, NIP: <strong>[NIP]</strong>.
        </p>
        <p>
          Kontakt w sprawach ochrony danych: e-mail <strong>[EMAIL_KONTAKTOWY]</strong>,
          telefon <strong>[TELEFON]</strong>.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">2. Cele i podstawy prawne</h2>
        <p>Państwa dane osobowe (imię, nazwisko, telefon, opcjonalnie e-mail) przetwarzamy w celu:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            <strong>Obsługi rezerwacji wizyty i wykonania usługi</strong> — art. 6 ust. 1 lit. b
            RODO (umowa).
          </li>
          <li>
            <strong>Wysyłki potwierdzeń i przypomnień SMS / e-mail</strong> związanych z wizytą —
            art. 6 ust. 1 lit. b RODO.
          </li>
          <li>
            <strong>Komunikacji marketingowej</strong> (oferty, promocje) — wyłącznie po
            wyrażeniu odrębnej zgody, art. 6 ust. 1 lit. a RODO.
          </li>
          <li>
            <strong>Wypełnienia obowiązków prawnych</strong> (m.in. księgowych) — art. 6 ust. 1
            lit. c RODO.
          </li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">3. Okres przechowywania</h2>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            Dane wizyt — przez okres wymagany przepisami (księgowymi) — co do zasady{' '}
            <strong>5 lat</strong> od końca roku obrachunkowego.
          </li>
          <li>
            Dane do komunikacji marketingowej — do momentu wycofania zgody.
          </li>
          <li>
            Dane kontaktowe — do momentu skorzystania z prawa do bycia zapomnianym (art. 17
            RODO). Po anonimizacji rekordy wizyt zostają w systemie bez danych osobowych klienta.
          </li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">4. Państwa prawa</h2>
        <p>Przysługują Państwu następujące prawa:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            <strong>Dostęp do danych</strong> (art. 15 RODO) — udostępniamy kopię posiadanych
            danych.
          </li>
          <li>
            <strong>Sprostowanie</strong> (art. 16 RODO) — poprawiamy nieprawidłowe lub
            niekompletne dane.
          </li>
          <li>
            <strong>Usunięcie danych — „prawo do bycia zapomnianym"</strong> (art. 17 RODO).
            Anonimizujemy dane osobowe w naszej bazie. Rekordy wizyt zostają w celach
            księgowych, ale są pozbawione informacji identyfikujących klienta.
          </li>
          <li>
            <strong>Ograniczenie przetwarzania</strong> (art. 18 RODO).
          </li>
          <li>
            <strong>Przenoszenie danych</strong> (art. 20 RODO) — wydajemy Państwa dane w
            formacie JSON.
          </li>
          <li>
            <strong>Sprzeciw</strong> wobec przetwarzania (art. 21 RODO).
          </li>
          <li>
            <strong>Cofnięcie zgody</strong> w dowolnym momencie (bez wpływu na zgodność z
            prawem przetwarzania dokonanego przed cofnięciem).
          </li>
          <li>
            <strong>Skarga do organu nadzorczego</strong> — Prezes Urzędu Ochrony Danych
            Osobowych, ul. Stawki 2, 00-193 Warszawa.
          </li>
        </ul>
        <p>
          Aby skorzystać z któregokolwiek z praw, prosimy o kontakt:{' '}
          <strong>[EMAIL_KONTAKTOWY]</strong>.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">5. Odbiorcy danych</h2>
        <p>Państwa dane mogą być udostępniane wyłącznie:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            Dostawcy hostingu i utrzymania systemu CRM (przetwarzają dane na nasze polecenie).
          </li>
          <li>
            Dostawcom bramek SMS / e-mail (jeżeli korzystamy z takich usług dla potwierdzeń —
            np. SMSAPI, własna bramka GSM, dostawca SMTP).
          </li>
          <li>
            Biuru rachunkowemu, organom państwowym i instytucjom uprawnionym przez prawo.
          </li>
        </ul>
        <p>Nie przekazujemy danych poza Europejski Obszar Gospodarczy.</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">6. Cookies</h2>
        <p>Strona używa wyłącznie plików cookies niezbędnych do działania:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>Cookie sesji logowania pracowników (panel CRM).</li>
          <li>Cookie wyboru języka interfejsu.</li>
        </ul>
        <p>
          Nie używamy cookies marketingowych ani analitycznych. Nie profilujemy odwiedzających.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">7. Bezpieczeństwo</h2>
        <p>
          Stosujemy odpowiednie środki techniczne i organizacyjne chroniące Państwa dane przed
          nieuprawnionym dostępem, w tym: szyfrowanie haseł, dostęp ograniczony rolami,
          szyfrowane połączenie HTTPS oraz regularne kopie zapasowe.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">8. Zmiany polityki</h2>
        <p>
          Polityka prywatności może być aktualizowana. Aktualna wersja jest zawsze dostępna pod
          adresem tej strony. O istotnych zmianach poinformujemy klientów drogą e-mail (jeżeli
          podali adres e-mail) lub poprzez komunikat w salonie.
        </p>
      </section>
    </article>
  );
}
